import { useState } from 'react';
import Button from 'src/components/buttons/button';
import config from 'config/config';
import Icon from 'src/components/icon';

const PROGRAM_URL = `${config.cdn.navigator}living-with-high-blood-pressure/`;

const getMenu = () => (
  <section className="navigator__contents">
    <div className="navigator__contents-header">Contents</div>
    <ul className="navigator__contents__list">
      <li><div className="navigator__contents__number">1.</div><div className="navigator__contents__content-text"><a href="#1">BHF Healthy Eating</a></div></li>
      <li><div className="navigator__contents__number">2.</div><div className="navigator__contents__content-text"><a href="#2">Expert advice on eating well</a></div></li>
      <li><div className="navigator__contents__number">3.</div><div className="navigator__contents__content-text"><a href="#3">Know your portion sizes</a></div></li>
      <li><div className="navigator__contents__number">4.</div><div className="navigator__contents__content-text"><a href="#4">Your seasonal recipe inspiration</a></div></li>
    </ul>
  </section>
);

const getSeason = () => {
  const d = new Date();
  const month = d.getMonth();
  switch (month) {
    case 12:
    case 1:
    case 2:
      return 'WINTER';
    case 3:
    case 4:
    case 5:
      return 'SPRING';
    case 6:
    case 7:
    case 8:
      return 'SUMMER';
    case 9:
    case 10:
    case 11:
      return 'AUTUMN';
  }
};

const getPage = () => {
  const season = getSeason();
  return (
    <div className="navigator">
      <section className="navigator__content">
        <h1 className="navigator__title">
          {'Week 9: Eating well to lower your blood pressure'}
        </h1>
        <p>{`Eating well is about eating a wide variety of foods in the right amounts to give your body what it needs. This will help you bring down your blood pressure and protect your heart in the long run. Week 9 brings you handy guides, top tips and delicious recipes to get you started. Eating well doesn't have to be dull or mean going hungry.`}</p>
        <div className="visible-xs">{getMenu()}</div>

        <h2>How your food affects your blood pressure</h2>
        <section className="navigator__section-half">
          <p>
            One of the best things you can do for your blood pressure is to eat a healthy, balanced diet.
            No single food can bring your blood pressure to a healthy level,
            but eating the right foods in the right portions most of the time will slowly help you get where you need to be.
          </p>
          <p>
            This is because eating well will help you maintain a healthy weight, which will avoid putting further stress on your heart.
            A healthy diet will also help you avoid too much salt which is linked with high blood pressure.
          </p>
          <p>
            Get the BHF guidance on what your body needs to work at its best,
            where you should cut back, and how to make easy food swaps that will let you enjoy the things you love in a healthier way.
          </p>
        </section>
        <img alt="Green and red vegetables in the shape of a heart" className="navigator__image-half" src={`${PROGRAM_URL}wk9-veg.jpg`} />
        <a href="https://www.bhf.org.uk/heart-health/preventing-heart-disease/healthy-eating" rel="noreferrer" target="_blank">
          <Button
            icon="open-right"
            iconLeftAligned={false}
            text="Learn more here"
          />
        </a>

        <h2>Expert advice on eating well</h2>
        <p>{`With all the newspapers, cookbooks, blogs and healthy-eating guides out there, it can be hard to know where to start if you're changing your diet.`}</p>
        <p>{`Try and get your information from the NHS, a recognised health charity like the BHF, or a dietitian. Dietitians are properly qualified and accredited, whereas nutritionists, nutritional therapists, nutrition experts and others don't have to be.`}</p>
        <p>
          All the advice we give at the British Heart Foundation is approved by our expert team of dietitians.
          See what our BHF Dietitian Victoria Taylor found out when she spoke to a community dietitian,
          a hospital dietitian and a cardiac rehab dietitian to get their best advice for those looking for a heart healthy diet.
        </p>
        <a href="https://www.bhf.org.uk/heart-matters-magazine/nutrition/heart-healthy-diet/community-dietitian" rel="noreferrer" target="_blank">
          Get advice from a community dietitian {'>'}
        </a>

        <h2>Know your portion sizes</h2>
        <img alt="16 examples of portion sizes" className="navigator__image-full" src={`${PROGRAM_URL}wk9-portions.jpg`} />
        <p>
          Eating the right amount of food is just as important as the types of food you eat.
          Women who are trying to stay the same weight should aim to eat around 2,000 calories a day,
          and men trying to stay the same weight should aim for 2,500 calories.
          These calories should come from lots of different, heart-healthy foods throughout the day.
        </p>
        <p>
          A portion size is how much you should eat of that food in one meal.
          Generally people eat a lot more than they need to feel full,
          and one of the easiest ways to achieve a healthy weight is to get your portion sizes right.
          Using smaller plates and glasses will all help you, but have a look at these portion sizes below and start getting used to using them.
        </p>
        <p>
          With packaged food, always read the label to find out how much one portion is.
          Make sure most of your plate is made up of vegetables and fruit,
          then a portion of starchy carbohydrates, with a smaller amount of protein and just a little bit of healthy fats.
        </p>
        <a href="https://www.bhf.org.uk/heart-matters/healthy-eating-toolkit/portion-guide" rel="noreferrer" target="_blank">
          See our handy portion size guides {'>'}
        </a>

        <div>
          {season === 'WINTER' && (
            <div>
              <h2>Chunky vegetable and bean goulash</h2>
              <section className="navigator__section-half">
                <p>
                  This wholesome goulash is the perfect warming winter meal.
                  It packs in lots of veg, while the beans make it proper comfort food.
                  This recipe makes four servings, and you can always reheat some portions the next day.
                </p>
              </section>
              <img alt="Goulash" className="navigator__image-half" src={`${PROGRAM_URL}wk9_winter.jpg`} />
              <a href="https://www.bhf.org.uk/heart-matters/healthy-eating-toolkit/recipe-finder/chunky-vegetable-and-bean-goulash" rel="noreferrer" target="_blank">
                <Button
                  icon="open-right"
                  iconLeftAligned={false}
                  text="Get the recipe"
                />
              </a>
              <a href="https://www.bhf.org.uk/heart-matters/healthy-eating-toolkit/recipe-finder" rel="noreferrer" target="_blank">
                <Button
                  icon="open-right"
                  iconLeftAligned={false}
                  text="Find more heart healthy recipes"
                />
              </a>
            </div>
          )}
          {season === 'SPRING' && (
            <div>
              <h2>Red onion, courgette and pepper pizza</h2>
              <section className="navigator__section-half">
                <p>
                  Watch a step-by-step cooking video of this healthy pizza alternative.
                  This is a great swap for takeaway pizza because it has more veg, less salt and fewer calories.
                </p>
              </section>
              <img alt="Red onion, courgette and pepper pizza" className="navigator__image-half" src={`${PROGRAM_URL}wk9_spring.jpg`} />
              <a href="https://www.bhf.org.uk/heart-matters/healthy-eating-toolkit/recipe-finder/red-onion-courgette-and-pepper-pizza" rel="noreferrer" target="_blank">
                <Button
                  icon="open-right"
                  iconLeftAligned={false}
                  text="Get the recipe"
                />
              </a>
              <a href="https://www.bhf.org.uk/heart-matters/healthy-eating-toolkit/recipe-finder" rel="noreferrer" target="_blank">
                <Button
                  icon="open-right"
                  iconLeftAligned={false}
                  text="Find more heart healthy recipes"
                />
              </a>
            </div>
          )}
          {season === 'SUMMER' && (
            <div>
              <h2>Summer fruit smoothie ice lollies</h2>
              <section className="navigator__section-half">
                <p>{`Simple but delicious, these fruit smoothie lollies are a cooling treat on a hot day and they're lower in sugar and fats than shop-bought ices. You can make them with different fresh fruit or other low-fat fruit yoghurts too. `}</p>
              </section>
              <img alt="Summer fruit smoothie ice lollies" className="navigator__image-half" src={`${PROGRAM_URL}wk9_summer.jpg`} />
              <a href="https://www.bhf.org.uk/heart-matters/healthy-eating-toolkit/recipe-finder/summer-fruit-smoothie-ice-lollies" rel="noreferrer" target="_blank">
                <Button
                  icon="open-right"
                  iconLeftAligned={false}
                  text="Get the recipe"
                />
              </a>
              <a href="https://www.bhf.org.uk/heart-matters/healthy-eating-toolkit/recipe-finder" rel="noreferrer" target="_blank">
                <Button
                  icon="open-right"
                  iconLeftAligned={false}
                  text="Find more heart healthy recipes"
                />
              </a>
            </div>
          )}
          {season === 'AUTUMN' && (
            <div>
              <h2>Chicken and vegetable tray bake</h2>
              <section className="navigator__section-half">
                <p>{`This tray bake is delicious with crusty bread or a side salad. It's also low in saturated fat, salt and sugars.`}</p>
              </section>
              <img alt="Chicken and vegetable tray bake" className="navigator__image-half" src={`${PROGRAM_URL}wk9_autumn.jpg`} />
              <a href="https://www.bhf.org.uk/heart-matters/healthy-eating-toolkit/recipe-finder/chicken-and-vegetable-traybake" rel="noreferrer" target="_blank">
                <Button
                  icon="open-right"
                  iconLeftAligned={false}
                  text="Get the recipe"
                />
              </a>
              <a href="https://www.bhf.org.uk/heart-matters/healthy-eating-toolkit/recipe-finder" rel="noreferrer" target="_blank">
                <Button
                  icon="open-right"
                  iconLeftAligned={false}
                  text="Find more heart healthy recipes"
                />
              </a>
            </div>
          )}
        </div>
        <img alt="Content provided by BHF" className="navigator__image-full navigator__banner-footer" src={`${PROGRAM_URL}bhf-content.jpg`} />
      </section>
    </div>
  );
};

function Footer() {
  const [visible, setVisible] = useState(true);

  if (!visible) return null;
  return (
    <div className="navigator__footer">
      <img alt="HealthUnlocked British Heart Foundation community screenshot" className="navigator__footer__image" src={`${PROGRAM_URL}cta-community.png`} />
      <div className="navigator__footer__inner">
        <div className="navigator__footer__title">
          Join the conversation on HealthUnlocked
        </div>
        <div className="navigator__footer__description">
          {`To get the most out of your heart health journey, you'll need support. Our online community on HealthUnlocked brings patients, their loved-ones and healthcare experts all together online to share advice and encouragement.`}
          {' '}
          <a
            className="navigator__footer__link"
            href="https://healthunlocked.com/bhf"
            rel="noreferrer"
            target="_blank"
          >
            Join the conversation {'>'}
          </a>
        </div>
        <div className="navigator__footer__close" onClick={() => setVisible(false)}>
          <Icon icon="cross" />
        </div>
      </div>
    </div>
  );
}

const obj = {
  getPage,
  getMenu,
  Footer,
  getSeoTitle: 'Living with high blood pressure | HealthUnlocked',
};

export default obj;
